import { useEffect, useMemo } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  DateRangePicker,
  StatsTabs,
  type DateRange,
  type StatsTab,
} from "../../features/statsNew/components";
import { selectDateRangeAsDates, setDateRange } from "../../features/statsNew/state/statsNewSlice";
import {
  AnonymousSessionCounter,
  ANONYMOUS_SESSION_COUNTER_COMPONENT,
} from "../../features/subscription/components/AnonymousSessionsCounter";
import Badge from "../../common/components/Badge/Badge";
import { fetchAnonymousSessionsCount } from "../../features/subscription/state/subscriptionSlice";
import "../../features/statsNew/components/statsNew.css";

const StatisticsLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();

  // Get state from Redux
  const dateRange = useSelector(selectDateRangeAsDates);

  // Fetch anonymous sessions count for paywall notification
  useEffect(() => {
    dispatch(
      fetchAnonymousSessionsCount({ componentId: ANONYMOUS_SESSION_COUNTER_COMPONENT }) as any,
    );
  }, [dispatch]);

  // Determine active tab based on current route
  const getActiveTab = (): StatsTab => {
    if (location.pathname.includes("/apprenants")) return "apprenants";
    if (location.pathname.includes("/parcours")) return "parcours";
    if (location.pathname.includes("/classement")) return "classement";
    if (location.pathname.includes("/comparateur")) return "comparateur";
    if (location.pathname.includes("/reports")) return "reports";
    return "global";
  };

  const activeTab = getActiveTab();

  // Memoize the outlet context to prevent unnecessary re-renders and pagination resets
  const outletContext = useMemo(() => ({ dateRange }), [dateRange]);

  // Update date range in Redux
  const handleDateRangeChange = (newRange: DateRange) => {
    dispatch(
      setDateRange({
        startDate: newRange.startDate ? newRange.startDate.toISOString() : null,
        endDate: newRange.endDate ? newRange.endDate.toISOString() : null,
      }),
    );
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-2">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
          <a href="/" className="hover:text-gray-700">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
              />
            </svg>
          </a>
          <span>/</span>
          <span>{t("statsNew.title")}</span>
        </div>

        <h1 className="text-xl font-bold text-gray-900 mb-2">
          {t("statsNew.title")}
          <span className="relative inline-block ml-2 align-middle group">
            <Badge
              color="bg-blue-100"
              textColor="text-blue-700"
              text="BETA"
              className="text-[10px] cursor-help"
            />
            <div className="absolute left-0 top-full mt-1 z-50 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none">
              <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 w-72 shadow-lg whitespace-normal font-normal">
                {t("statsNew.betaNotice")}
              </div>
            </div>
          </span>
        </h1>

        {/* Navigation and date picker */}
        <div className="flex items-center justify-between gap-4">
          <StatsTabs activeTab={activeTab} />

          {/* Date picker */}
          <DateRangePicker value={dateRange} onChange={handleDateRangeChange} />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 p-4 flex flex-col">
        {/* Paywall notification for unpaid anonymous sessions */}
        <AnonymousSessionCounter
          className="flex-shrink-0 bg-yellow-100 w-full pt-2 pb-3 rounded-lg mb-3"
          withSpinner={false}
        />
        <div className="flex-1 min-h-0">
          <Outlet context={outletContext} />
        </div>
      </div>
    </div>
  );
};

export default StatisticsLayout;
